import { useAuth } from '@ixigo-packages/login/react';
import { useEffect } from 'react';
import { logCbccCvp } from '@/lib/loki-logger';
import { cbccRouteApi } from '../route-api';
import {
	CBCC_EVENT,
	setCbccCommonProps,
	trackCbccEvent,
} from '../services/analytics';
import { HOME_SCREEN_NAME } from '../types';

// Single-shot per JS context: the CVP Page Load event must fire exactly ONCE per
// landing-page visit, even though the page subtree REMOUNTS mid-load. A logged-in
// user with an expired access token boots in auth status `resolving`; after the
// refresh-token restore settles it to `authenticated`, the status flip fires
// router.invalidate() (see routes/__root.tsx), which re-runs the loader AND
// remounts this component — so a mount-coupled effect fires twice (the production
// duplicate). A useRef guard resets on that remount; this module-level flag
// survives it. A hard reload gets a fresh module = a fresh visit.
let cvpPageLoadFired = false;
function consumeCvpPageLoadOnce(): boolean {
	if (cvpPageLoadFired) return false;
	cvpPageLoadFired = true;
	return true;
}

// Null-render side-effect component, mounted once in the layout. Two jobs:
//
// 1. Loki: a fire-and-forget info log to `/logs/ui` on EVERY client landing-page
//    load (and again after an in-page login re-resolves CVP):
//     - logged out → `{ isLoggedIn: false }` (CVP isn't fetched — `at`-gated);
//     - logged in  → `{ isLoggedIn: true, status, userFlow, ctaText }`.
//
// 2. CleverTap: keep the CVP-derived common props (User Status / Onboarding
//    Eligibility / isUserLoggedIn) in sync for ALL cbcc events (setCbccCommonProps,
//    reactive — runs before any event so they carry the latest CVP), and fire
//    the once-per-visit `CBCC CVP Page Load` (+ `CBCC Back to ixigo` when a
//    `source` param is present). Page Load fires once auth has settled and is
//    de-duped by a module-level flag — it must NOT re-fire on the post-login
//    invalidate-remount or when CVP fields change, unlike the Loki log.
export function CbccCvpLogger() {
	// `authStatus` is the SDK auth lifecycle ('resolving' | 'authenticated' |
	// 'anonymous'); distinct from the CVP screen `status` (User Status) below.
	const { isLoggedIn, status: authStatus } = useAuth();
	const { screens, userFlow } = cbccRouteApi.useLoaderData();
	const homeScreen = screens?.find(
		(screen) => screen.name === HOME_SCREEN_NAME,
	);
	const status = homeScreen?.status;
	const ctaText = homeScreen?.ctaText;
	const userEligibleToApply = homeScreen?.userEligibleToApply;

	// Loki log — re-fires whenever CVP fields change (incl. post-login).
	useEffect(() => {
		logCbccCvp({
			isLoggedIn,
			// undefined for logged-out / unresolved CVP — the logger/flatten layer
			// drops empty fields, so the record is just `{ isLoggedIn: false }`.
			status,
			userFlow,
			ctaText,
		});
	}, [isLoggedIn, status, userFlow, ctaText]);

	// CleverTap common props — sync on every CVP/login change so subsequent
	// events carry the latest values. (`isUserLoggedIn` is the analytics event
	// property name; map the SDK's `isLoggedIn` onto it.)
	useEffect(() => {
		setCbccCommonProps({
			status,
			userEligibleToApply,
			isUserLoggedIn: isLoggedIn,
		});
	}, [status, userEligibleToApply, isLoggedIn]);

	// CleverTap CVP Page Load (+ Back to ixigo) — ONCE per page visit. Gated on
	// SETTLED auth (status !== 'resolving'), NOT on mount: a logged-in user with an
	// expired token boots at `resolving` (CVP not yet resolved → would fire with
	// logged-out commons), then the refresh settles → router.invalidate() remounts
	// this component. Skipping while `resolving` means we fire on the settled
	// mount, carrying correct logged-in commons; the module-level single-shot
	// (consumeCvpPageLoadOnce) stops the remount from double-firing. Logged-out
	// users boot already-settled (`anonymous`), so they fire immediately. The
	// common-props effect above runs first on the same render, so commons are set.
	useEffect(() => {
		if (authStatus === 'resolving') return;
		if (!consumeCvpPageLoadOnce()) return;
		trackCbccEvent(CBCC_EVENT.CVP_PAGE_LOAD);
		const source = new URLSearchParams(window.location.search).get('source');
		if (source) trackCbccEvent(CBCC_EVENT.BACK_TO_IXIGO, { source });
	}, [authStatus]);

	return null;
}
